/**
 * 오답노트 저장 시스템
 * 사용자별로 틀린 문제를 localStorage에 저장하고 관리
 */

// 오답 저장
export const saveWrongAnswer = (userId, questionData) => {
  if (!userId || !questionData) return false;

  try {
    const storageKey = `wrongAnswers_${userId}`;
    const existingWrongAnswers = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    // 중복 확인 (같은 문제는 최신 것만 유지)
    const existingIndex = existingWrongAnswers.findIndex(
      item => item.questionId === questionData.questionId
    );
    
    const wrongAnswerEntry = {
      questionId: questionData.questionId,
      questionText: questionData.questionText,
      options: questionData.options,
      correctAnswer: questionData.correctAnswer,
      userAnswer: questionData.userAnswer,
      explanation: questionData.explanation,
      examType: questionData.examType,
      subject: questionData.subject,
      difficulty: questionData.difficulty,
      image: questionData.image,
      tags: questionData.tags,
      year: questionData.year,
      session: questionData.session,
      questionNumber: questionData.questionNumber,
      wrongDate: new Date().toISOString(),
      attempts: (existingIndex >= 0 ? existingWrongAnswers[existingIndex].attempts : 0) + 1
    };

    if (existingIndex >= 0) {
      // 기존 항목 업데이트
      existingWrongAnswers[existingIndex] = wrongAnswerEntry;
    } else {
      // 새 항목 추가 (최신순으로 맨 앞에)
      existingWrongAnswers.unshift(wrongAnswerEntry);
    }

    // 최대 500개까지만 저장 (용량 관리)
    if (existingWrongAnswers.length > 500) {
      existingWrongAnswers.splice(500);
    }

    localStorage.setItem(storageKey, JSON.stringify(existingWrongAnswers));
    
    // 커스텀 이벤트 발생 (다른 컴포넌트에서 실시간 업데이트 가능)
    window.dispatchEvent(new CustomEvent('wrongAnswerAdded', {
      detail: { userId, wrongAnswerEntry }
    }));

    return true;
  } catch (error) {
    console.error('오답 저장 오류:', error);
    return false;
  }
};

// 오답 목록 조회
export const getWrongAnswers = (userId, filters = {}) => {
  if (!userId) return [];

  try {
    const storageKey = `wrongAnswers_${userId}`;
    const wrongAnswers = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    let filteredAnswers = wrongAnswers;

    // 과목 필터
    if (filters.examType && filters.examType !== 'all') {
      filteredAnswers = filteredAnswers.filter(item => item.examType === filters.examType);
    }

    // 날짜 필터
    if (filters.dateFrom) {
      filteredAnswers = filteredAnswers.filter(item => 
        new Date(item.wrongDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filteredAnswers = filteredAnswers.filter(item => 
        new Date(item.wrongDate) <= new Date(filters.dateTo)
      );
    }

    // 정렬 (최신순)
    filteredAnswers.sort((a, b) => new Date(b.wrongDate) - new Date(a.wrongDate));

    return filteredAnswers;
  } catch (error) {
    console.error('오답 조회 오류:', error);
    return [];
  }
};

// 오답 삭제
export const deleteWrongAnswer = (userId, questionId) => {
  if (!userId || !questionId) return false;

  try {
    const storageKey = `wrongAnswers_${userId}`;
    const wrongAnswers = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    const filteredAnswers = wrongAnswers.filter(item => item.questionId !== questionId);
    localStorage.setItem(storageKey, JSON.stringify(filteredAnswers));
    
    // 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('wrongAnswerDeleted', {
      detail: { userId, questionId }
    }));

    return true;
  } catch (error) {
    console.error('오답 삭제 오류:', error);
    return false;
  }
};

// 오답 통계
export const getWrongAnswerStats = (userId) => {
  if (!userId) return null;

  try {
    const wrongAnswers = getWrongAnswers(userId);
    
    const stats = {
      totalWrongAnswers: wrongAnswers.length,
      examTypeBreakdown: {},
      subjectBreakdown: {},
      recentActivity: [],
      improvementRate: 0
    };

    // 과목별 분석
    wrongAnswers.forEach(item => {
      stats.examTypeBreakdown[item.examType] = (stats.examTypeBreakdown[item.examType] || 0) + 1;
      if (item.subject) {
        stats.subjectBreakdown[item.subject] = (stats.subjectBreakdown[item.subject] || 0) + 1;
      }
    });

    // 최근 7일 활동
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    stats.recentActivity = wrongAnswers
      .filter(item => new Date(item.wrongDate) >= weekAgo)
      .slice(0, 10);

    // 개선률 계산 (재시도 횟수가 있는 문제들의 비율)
    const retriedQuestions = wrongAnswers.filter(item => item.attempts > 1);
    stats.improvementRate = wrongAnswers.length > 0 
      ? Math.round((retriedQuestions.length / wrongAnswers.length) * 100)
      : 0;

    return stats;
  } catch (error) {
    console.error('오답 통계 조회 오류:', error);
    return null;
  }
};

// 오답 초기화 (전체 삭제)
export const clearAllWrongAnswers = (userId) => {
  if (!userId) return false;

  try {
    const storageKey = `wrongAnswers_${userId}`;
    localStorage.removeItem(storageKey);
    
    // 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('wrongAnswersCleared', {
      detail: { userId }
    }));

    return true;
  } catch (error) {
    console.error('오답 초기화 오류:', error);
    return false;
  }
};

// 오답 내보내기 (JSON)
export const exportWrongAnswers = (userId) => {
  if (!userId) return null;

  try {
    const wrongAnswers = getWrongAnswers(userId);
    const exportData = {
      userId,
      exportDate: new Date().toISOString(),
      wrongAnswers,
      stats: getWrongAnswerStats(userId)
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    return {
      blob: dataBlob,
      filename: `오답노트_${userId}_${new Date().toISOString().split('T')[0]}.json`
    };
  } catch (error) {
    console.error('오답 내보내기 오류:', error);
    return null;
  }
};